import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/** Which reply-layout editors on the agent page currently hold a layout that can't be saved. The form only
 *  ever receives valid layouts, so an editor with problems would otherwise let Save store the last valid
 *  one without a word; the page asks first (same pattern as the prompt template checks). */
export interface LayoutDrafts {
  report: (id: string, ok: boolean) => void;
  invalidCount: () => number;
}

const NONE: LayoutDrafts = { report: () => undefined, invalidCount: () => 0 };
export const LayoutDraftsContext = createContext<LayoutDrafts>(NONE);

export const useLayoutDraftsModel = (): LayoutDrafts => {
  const invalid = useRef(new Set<string>());
  const report = useCallback((id: string, ok: boolean) => {
    if (ok) invalid.current.delete(id);
    else invalid.current.add(id);
  }, []);
  const invalidCount = useCallback(() => invalid.current.size, []);
  return useMemo(() => ({ report, invalidCount }), [report, invalidCount]);
};

/** Report one editor's validity while it is mounted (an unmounted editor is no longer a problem). */
export const useReportLayoutValidity = (id: string, ok: boolean) => {
  const { report } = useContext(LayoutDraftsContext);
  useEffect(() => {
    report(id, ok);
  }, [report, id, ok]);
  useEffect(() => () => report(id, true), [report, id]);
};
